import { useMemo, useState, type ReactNode } from 'react'
import {
  getLegacyManagementMonthRange,
  legacyWorkScheduleManagementOffices,
  legacyWorkScheduleManagementRows,
  legacyWorkScheduleManagementStatuses,
  type LegacyManagementScheduleRow,
  type LegacyManagementScheduleType,
} from '../../mock/legacyWorkScheduleManagement'
import './LegacyWorkScheduleManagement.css'

const PAGE_SIZES = [30, 60, 100, 500, 1000] as const

type AppliedFilters = {
  type: LegacyManagementScheduleType
  officeId: string
  startDate: string
  endDate: string
  statuses: string[]
}

function isScheduleType(value: string | null): value is LegacyManagementScheduleType {
  return value === '1' || value === '2'
}

function initialOffice(params: URLSearchParams) {
  const officeId = params.get('office_id')
  const officeName = params.get('office_name')
  return legacyWorkScheduleManagementOffices.find((office) => office.id === officeId)
    ?? legacyWorkScheduleManagementOffices.find((office) => office.name === officeName)
    ?? legacyWorkScheduleManagementOffices[0]
}

function initialStatuses(params: URLSearchParams) {
  const allowed = new Set(legacyWorkScheduleManagementStatuses.map((status) => status.code))
  const selected = (params.get('comp_yn') ?? '1').split(',').filter((code) => allowed.has(code))
  return selected.length > 0 ? selected : ['1']
}

function initialPageSize(params: URLSearchParams) {
  const value = Number(params.get('page_count') ?? 30)
  return PAGE_SIZES.includes(value as (typeof PAGE_SIZES)[number]) ? value : 30
}

function DetailButton({ children, onClick, label }: { children: ReactNode; onClick: () => void; label: string }) {
  return <button className="l_schedule_info_text l_caller_p" type="button" onClick={onClick} aria-label={label}>{children}</button>
}

export function LegacyWorkScheduleManagement() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const monthRange = useMemo(() => getLegacyManagementMonthRange(), [])
  const officeFromUrl = useMemo(() => initialOffice(params), [params])
  const typeFromUrl = isScheduleType(params.get('type')) ? params.get('type') as LegacyManagementScheduleType : '1'
  const statusesFromUrl = useMemo(() => initialStatuses(params), [params])
  const pageSizeFromUrl = useMemo(() => initialPageSize(params), [params])
  const startDateFromUrl = params.get('start_date') ?? monthRange.startDate
  const endDateFromUrl = params.get('end_date') ?? monthRange.endDate

  const [type, setType] = useState<LegacyManagementScheduleType>(typeFromUrl)
  const [officeId, setOfficeId] = useState(officeFromUrl.id)
  const [startDate, setStartDate] = useState(startDateFromUrl)
  const [endDate, setEndDate] = useState(endDateFromUrl)
  const [statuses, setStatuses] = useState<string[]>(statusesFromUrl)
  const [applied, setApplied] = useState<AppliedFilters>({
    type: typeFromUrl,
    officeId: officeFromUrl.id,
    startDate: startDateFromUrl,
    endDate: endDateFromUrl,
    statuses: statusesFromUrl,
  })
  const [pageSize, setPageSize] = useState(pageSizeFromUrl)
  const [page, setPage] = useState(Math.max(1, Number(params.get('page_no') ?? 1) || 1))

  const filteredRows = useMemo(() => legacyWorkScheduleManagementRows.filter((row) => (
    row.type === applied.type
    && row.officeId === applied.officeId
    && row.date >= applied.startDate
    && row.date <= applied.endDate
    && applied.statuses.includes(row.statusCode)
  )), [applied])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const firstIndex = (currentPage - 1) * pageSize
  const visibleRows = filteredRows.slice(firstIndex, firstIndex + pageSize)
  const dataStart = filteredRows.length === 0 ? 0 : firstIndex + 1
  const dataEnd = Math.min(firstIndex + pageSize, filteredRows.length)
  const firstPageInGroup = Math.floor((currentPage - 1) / 10) * 10 + 1
  const lastPageInGroup = Math.min(totalPages, firstPageInGroup + 9)
  const pageNumbers = Array.from({ length: lastPageInGroup - firstPageInGroup + 1 }, (_, index) => firstPageInGroup + index)
  const allStatusCodes = legacyWorkScheduleManagementStatuses.map((status) => status.code)
  const allStatusesSelected = statuses.length === allStatusCodes.length

  const writeUrl = (filters: AppliedFilters, nextPage: number, nextPageSize: number) => {
    const url = new URL(window.location.href)
    url.searchParams.set('type', filters.type)
    url.searchParams.set('office_id', filters.officeId)
    url.searchParams.set('comp_yn', filters.statuses.join(','))
    url.searchParams.set('start_date', filters.startDate)
    url.searchParams.set('end_date', filters.endDate)
    url.searchParams.set('page_no', String(nextPage))
    url.searchParams.set('page_start', String(Math.floor((nextPage - 1) / 10) * 10 + 1))
    url.searchParams.set('page_count', String(nextPageSize))
    url.searchParams.set('is_search', '1')
    window.history.replaceState({}, '', `${url.pathname}${url.search}`)
  }

  const search = () => {
    if (!startDate || !endDate) {
      window.alert('시작일자, 종료일자는 필수값 입니다.')
      return
    }
    if (startDate > endDate) {
      window.alert('종료일자가 시작일자 보다 앞설 수 없습니다.')
      return
    }
    if (statuses.length === 0) {
      window.alert('한 개 이상의 일정 상태를 선택해주세요.')
      return
    }

    const nextFilters = { type, officeId, startDate, endDate, statuses: [...statuses] }
    setApplied(nextFilters)
    setPage(1)
    writeUrl(nextFilters, 1, pageSize)
  }

  const toggleStatus = (code: string) => {
    setStatuses((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code])
  }

  const toggleAllStatuses = () => {
    setStatuses(allStatusesSelected ? [] : allStatusCodes)
  }

  const movePage = (nextPage: number) => {
    const normalized = Math.max(1, Math.min(totalPages, nextPage))
    setPage(normalized)
    writeUrl(applied, normalized, pageSize)
  }

  const changePageSize = (nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPage(1)
    writeUrl(applied, 1, nextPageSize)
  }

  const openDetail = (row: LegacyManagementScheduleRow) => {
    const query = new URLSearchParams({
      CRUD: '1',
      office_id: row.officeId,
      schedule_id: row.scheduleId,
      office_name: row.officeName,
      customer_id: row.customerId,
      working_group_id: row.workgroupId,
      staff_id: row.staffId,
    })
    window.open(`/v1/work_schedule?${query.toString()}`, '_blank', 'width=500,height=700,location=no,status=no,scrollbars=yes')
  }

  return <div className="l_work_schedule_list_management_wrap legacy-s03-management">
    <div className="g_title">일정 목록</div>
    <section className="g_filter_wrap">
      <div className="g_row"><div className="g_input_wrap required">
        <label className="g_label title center" htmlFor="l_office_id_select_chosen">지점</label>
        <select className="g_input l_input" id="l_office_id_select_chosen" value={officeId} onChange={(event) => setOfficeId(event.target.value)}>
          {legacyWorkScheduleManagementOffices.map((office) => <option key={office.id} value={office.id}>{office.name}</option>)}
        </select>
      </div></div>
      <div className="g_row"><div className="g_input_wrap required">
        <label className="g_label title center" htmlFor="l_start_date">일정 기간</label>
        <div className="l_date_picker_range_wrap">
          <input className="g_input l_input" id="l_start_date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          <p>~</p>
          <input className="g_input l_input" id="l_end_date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
      </div></div>
      <div className="g_row"><div className="g_input_wrap required l_has_inner_item_wrap">
        <span className="g_label title center">일정분류</span>
        <div className="l_schedule_type_item_wrap">
          <label><input className="g_input radiobox" type="radio" name="type" value="1" checked={type === '1'} onChange={() => setType('1')} /><span>업무일정</span></label>
          <label><input className="g_input radiobox" type="radio" name="type" value="2" checked={type === '2'} onChange={() => setType('2')} /><span>일반일정</span></label>
        </div>
      </div></div>
      <div className="g_row"><div className="g_input_wrap required l_has_inner_item_wrap">
        <span className="g_label title center">일정 상태</span>
        <div className="l_schedule_status_item_wrap">
          <label><input className="g_input checkbox" type="checkbox" checked={allStatusesSelected} onChange={toggleAllStatuses} /><span>전체</span></label>
          {legacyWorkScheduleManagementStatuses.map((status) => <label key={status.code}>
            <input className="g_input checkbox" type="checkbox" checked={statuses.includes(status.code)} onChange={() => toggleStatus(status.code)} />
            <span>{status.label}</span>
          </label>)}
        </div>
      </div></div>
      <div className="g_row"><button className="g_btn search l_margin_left l_work_sch_search_btn" type="button" onClick={search}>검색</button></div>
    </section>

    <section className="l_result_wrap">
      <div className="l_pagenation_item"><div className="g_row"><div className="g_page_limit" id="g_page_limit_wrap">
        Data : <span>{dataStart}</span> to <span>{dataEnd}</span> (전체 : <span>{filteredRows.length}</span>)
        <select className="g_input" id="g_limit_data" value={pageSize} onChange={(event) => changePageSize(Number(event.target.value))}>
          {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
        </select>
      </div></div></div>
      <div className="l_list_wrap"><div className="g_list_wrap"><table className="g_list l_work_schedule_list" id="l_work_schedule_list">
        <thead><tr><th>no</th><th>{applied.type === '1' ? '작업자' : '작업그룹'}</th><th>일자</th><th>시간</th><th>고객명</th><th>제목</th><th>내용</th><th>상태</th></tr></thead>
        <tbody>{visibleRows.length > 0 ? visibleRows.map((row, index) => {
          const open = () => openDetail(row)
          return <tr key={row.id} style={{ color: row.color }}>
            <td>{index + 1}</td>
            <td><DetailButton onClick={open} label={`${applied.type === '1' ? '업무' : '일반'} 일정 상세 열기`}>{applied.type === '1' ? row.worker : row.workgroup}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.date}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.time}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.customer}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.title}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.memo}</DetailButton></td>
            <td><DetailButton onClick={open} label="일정 상세 열기">{row.statusLabel}</DetailButton></td>
          </tr>
        }) : <tr><td className="l_empty_data_row" colSpan={8}>검색 결과가 없습니다. 검색 조건을 변경해보세요.</td></tr>}</tbody>
      </table></div></div>
      <div className="l_pagenation_item"><ul className="g_pagination">
        {currentPage > 1 && <li><button className="g_btn pagination size_page" type="button" onClick={() => movePage(currentPage - 1)} aria-label="이전 페이지">‹</button></li>}
        {pageNumbers.map((pageNumber) => <li key={pageNumber}><button className={`g_btn pagination size_page${pageNumber === currentPage ? ' active' : ''}`} type="button" aria-current={pageNumber === currentPage ? 'page' : undefined} onClick={() => movePage(pageNumber)}>{pageNumber}</button></li>)}
        {currentPage < totalPages && <li><button className="g_btn pagination size_page" type="button" onClick={() => movePage(currentPage + 1)} aria-label="다음 페이지">›</button></li>}
      </ul></div>
    </section>
  </div>
}
